import {
  DefaultFetchError,
  FetchCreate,
  FetchGet,
  FetchGetId,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";

export interface Donation {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface DonationTableRow extends Donation {
  key: string;
}

export interface DonationTableData {
  data: DonationTableRow[];
  total: number;
}

export interface DonationCreateParams {
  name: string;
  description: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface DonationUpdateParams {
  name?: string;
  description?: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface DonationGetParams
  extends PagingParams, Partial<Omit<Donation, "id">> {
  orderByColumn?: keyof Omit<Donation, "id">;
}

export const useDonationGet = <DD = PagingDataResponse<Donation>>(
  decorateData?: (data: PagingDataResponse<Donation>) => DD,
): FetchGet<
  PagingDataResponse<Donation>,
  DonationGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("donations", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useDonationTableGet = () =>
  useDonationGet<DonationTableData>(
    ({ data, meta }: PagingDataResponse<Donation>): DonationTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useDonationGetById = <DD = Donation>(
  id?: string,
  decorateData?: (data: Donation) => DD,
): FetchGetId<Donation, DefaultFetchError, unknown, DD> =>
  useFetchGetId("donations", id, {
    autoStart: !!id,
    decorateData,
  });

export const useDonationCreate = (): FetchCreate<
  DonationCreateParams,
  DefaultFetchError,
  DonationCreateParams
> => useFetchCreate("donations");

export const useDonationUpdate = (
  id?: string,
): FetchUpdate<DonationUpdateParams, DefaultFetchError, DonationUpdateParams> =>
  useFetchUpdate("donations", id);
